import { Model, Factory, belongsTo } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { rand } from '@ngneat/falso'

import type { SchemaAttributes } from '../types.js'
import type { MockServer, MirageServer } from './server.js'
import { sortable, pageable, getQueryParamValue } from './utils.js'

export type BreedsData = SchemaAttributes<'Breeds'>

export const model = (Model as ModelDefinition<BreedsData>).extend({
  species: belongsTo('species'),
})

const BREEDS_BY_SPECIES: Record<string, string[]> = {
  Cat: [
    'Abyssinian',
    'American Shorthair',
    'Bengal',
    'British Shorthair',
    'Maine Coon',
    'Persian',
    'Ragdoll',
    'Russian Blue',
    'Siamese',
    'Sphynx',
  ],
  Dog: [
    'Labrador Retriever',
    'Golden Retriever',
    'German Shepherd',
    'Bulldog',
    'Poodle',
    'Beagle',
    'Rottweiler',
    'Yorkshire Terrier',
    'Dachshund',
    'Siberian Husky',
  ],
  Bird: [
    'Budgerigar',
    'Canary',
    'Cockatiel',
    'Lovebird',
    'Parrot',
    'Finch',
    'Conure',
    'Macaw',
    'African Grey',
    'Cockatoo',
  ],
  Rabbit: [
    'Holland Lop',
    'Netherland Dwarf',
    'Mini Rex',
    'Lionhead',
    'Angora',
    'Flemish Giant',
    'Dutch',
    'English Lop',
    'Himalayan',
    'Jersey Wooly',
  ],
  Horse: [
    'Arabian',
    'Thoroughbred',
    'Quarter Horse',
    'Appaloosa',
    'Paint Horse',
    'Clydesdale',
    'Friesian',
    'Mustang',
    'Andalusian',
    'Tennessee Walker',
  ],
  Ferret: ['Domestic Ferret', 'Angora Ferret', 'European Polecat'],
  'Guinea Pig': [
    'American Guinea Pig',
    'Abyssinian Guinea Pig',
    'Peruvian Guinea Pig',
    'Silkie Guinea Pig',
  ],
  Hamster: [
    'Syrian Hamster',
    'Dwarf Hamster',
    'Chinese Hamster',
    'Roborovski Hamster',
  ],
  Fish: ['Goldfish', 'Betta', 'Guppy', 'Angelfish', 'Tetra', 'Molly'],
  Reptile: [
    'Bearded Dragon',
    'Leopard Gecko',
    'Ball Python',
    'Corn Snake',
    'Blue-tongued Skink',
  ],
}

export const factory = Factory.extend<BreedsData>({
  name() {
    const allBreeds = Object.values(BREEDS_BY_SPECIES).flat()
    return rand(allBreeds)
  },
  afterCreate(breed, server) {
    if (!breed.species) {
      // Find a species that has this breed
      const speciesName = Object.keys(BREEDS_BY_SPECIES).find((species) =>
        BREEDS_BY_SPECIES[species].includes(breed.name as string),
      )

      if (speciesName) {
        const species = server.schema.findBy('species', { name: speciesName })
        if (species) {
          breed.update({ species })
        }
      }
    }
  },
})

export function routes(server: MockServer) {
  server.get('/breeds', function (this: MirageServer, schema, request) {
    let breeds = schema.all('breed')

    // Filter by species_id if provided
    const speciesId = getQueryParamValue(request?.queryParams?.species_id)

    if (speciesId) {
      const numericSpeciesId = Number(speciesId)
      breeds = breeds.filter((breed) => {
        return Number(breed.species?.id) === numericSpeciesId
      })
    }

    const sorted = sortable(breeds, request)
    const paginated = pageable(sorted, request)
    return this.serialize(paginated.data)
  })
}

export function seeds(server: MockServer) {
  // Get all species from the database
  const allSpecies = server.schema.all('species')

  // Only create breeds if species exist
  if (allSpecies.length === 0) {
    console.warn(
      'No species found when seeding breeds. Species should be seeded first.',
    )
    return
  }

  // Create breeds for each species
  allSpecies.models.forEach((species) => {
    const speciesName = species.name as keyof typeof BREEDS_BY_SPECIES
    const breeds = BREEDS_BY_SPECIES[speciesName] || []

    breeds.forEach((breedName) => {
      server.create('breed', {
        name: breedName,
        species,
      })
    })
  })
}
